import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as moment from "moment";
import { FC, createRef, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import TableComponent from "../../../core/components/table/table.component";
import HasPermission from "../../../core/guards/has-permission";
import FilterFormComponent, {
  FilterFormConfig,
} from "../../../shared/filter-form/filter-form.component";
import {
  downLoadTestReportsFile,
  getEmergencyLightByBlockCode,
  setFilter,
} from "../../../store/slices/fault-module/test-report.slice";
import { getBlocksBySiteCode } from "../../../store/slices/location-block.slice";
import { getSiteByTCCode } from "../../../store/slices/location-site.slice";
import { getTownCouncils } from "../../../store/slices/town-council.slice";

import pdfIcon from "../../../assets/images/pdfBiomeIcon.svg";
import spreadsheetIcon from "../../../assets/images/spreadsheetIconBiome.svg";

import { B2Button } from "@hdb/biome-2";
interface TestReportTableComponentProps {
  columns: any;
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  onRefetchData: (
    tcCode: any,
    siteCode: any,
    blockCode: any,
    emergencyLight: any,
    startDate: any,
    endDate: any
  ) => void;
  isLoading: boolean;
}

const TestReportTableComponent: FC<TestReportTableComponentProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  onRefetchData,
  isLoading,
}) => {
  const formRef = createRef();
  const [tcOptions, setTCOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [blockOptions, setBlockOptions] = useState<any>([]);
  const [isFiltered, setIsFiltered] = useState<any>(false);
  const [emergencyLightOptions, setEmergencyLightOptions] = useState<any>([]);
  const [downloaDocumentPayload, setDownloaDocumentPayload] = useState<any>({
    townCouncil: "",
    site: "",
    block: "",
    emergencyLight: "",
    startDate: "",
    endDate: "",
  });

  const [filterUpdateCount, setFilterUpdateCount] = useState(0);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
  }, [dispatch]);

  const townCouncilData = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });

  const siteDataByTCCode = useSelector((state: any) => {
    return state.locationSite.sitesByTCCode;
  });

  const blockDataSiteCode = useSelector((state: any) => {
    return state.locationBlock.blocksBySiteCode;
  });

  const emergencyLightDataByBlockCode = useSelector((state: any) => {
    return state.testReport.emergencyLightByBlockCode;
  });

  const isLoadingEmergencyLightByBlockCode = useSelector((state: any) => {
    return state.testReport.isLoadingEmergencyLightByBlockCode;
  });

  const isLoadingTownCouncils = useSelector((state: any) => {
    return state.townCouncil.isLoadingTownCouncils;
  });

  const isLocationSitesByTCCode = useSelector((state: any) => {
    return state.locationSite.isLocationSitesByTCCode;
  });

  const isLoadingBlocksBySiteCode = useSelector((state: any) => {
    return state.locationBlock.isLoadingBlocksBySiteCode;
  });

  const isLoadingTestReportsDownload = useSelector((state: any) => {
    return state.testReport.isLoadingTestReportsDownload;
  });
  const [currentTC, setCurrentTC] = useState({ label: "", value: "" });

  useEffect(() => {
    const tcs = townCouncilData?.data?.map((tc: any) => ({
      label: tc?.name,
      value: tc?.tcCode,
    }));
    // if user is TC Admin only 1 tc is available
    if (tcs?.length === 1) {
      setCurrentTC(tcs[0]);
    }

    setTCOptions(tcs);
    setFilterUpdateCount(filterUpdateCount + 1);
  }, [townCouncilData]);

  useEffect(() => {
    if (currentTC.value.length) {
      setFormFilterInitialValues((prev) => ({
        ...prev,
        townCouncil: currentTC.value,
      }));

      dispatch(getSiteByTCCode({ tcCode: currentTC.value }));
    }
  }, [currentTC]);

  useEffect(() => {
    const sites = siteDataByTCCode?.data?.map((site: any) => ({
      label: site?.name,
      value: site?.siteCode,
    }));

    if (!sites?.length) return;

    setSiteOptions(sites);
  }, [siteDataByTCCode]);

  useEffect(() => {
    const blocks = blockDataSiteCode?.data?.map((block: any) => ({
      label: block?.blockName,
      value: block?.blockCode,
    }));

    setBlockOptions(blocks);
  }, [blockDataSiteCode]);

  useEffect(() => {
    const lights = emergencyLightDataByBlockCode?.data?.map((light: any) => ({
      label: light?.assetCode,
      value: light?.assetCode,
    }));

    setEmergencyLightOptions(lights);
  }, [emergencyLightDataByBlockCode]);

  useEffect(() => {
    setSiteOptions([]);
    setBlockOptions([]);
  }, []);

  const onChangeTC = (selectedTC: any) => {
    setSiteOptions([]);
    setBlockOptions([]);

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      townCouncil: selectedTC,
      site: "",
      block: "",
      emergencyLight: "",
    }));
    // const form: any = formRef.current;

    // if (form) {
    //   // Get all field names except 'towncouncil'
    //   const fieldNames = Object.keys(form.values).filter(
    //     (name) => name !== "towncouncil"
    //   );

    //   // Reset each field to empty, except 'towncouncil'
    //   fieldNames.forEach((fieldName) => {
    //     if (fieldName === "site" || fieldName === "block") {
    //       form.setFieldValue(fieldName, "", false); // false to avoid validation after setting field
    //     }
    //   });

    // }

    if (!selectedTC) return;

    dispatch(getSiteByTCCode({ tcCode: selectedTC }));
  };

  const onChangeSite = (selectedSite: any) => {
    setBlockOptions([]);
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      site: selectedSite,
      block: "",
      emergencyLight: "",
    }));

    if (!selectedSite) return;

    dispatch(getBlocksBySiteCode({ siteCode: selectedSite }));
  };

  const onChangeBlock = (selectedBlock: any) => {
    setEmergencyLightOptions([]);

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      block: selectedBlock,
      emergencyLight: "",
    }));

    if (!selectedBlock) return;

    dispatch(getEmergencyLightByBlockCode({ blockCode: selectedBlock }));
  };

  const onChangeStartDate = (startDate: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      startDate: startDate,
    }));
  };
  const onChangeEndDate = (endDate: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      endDate: endDate,
    }));
  };

  const onSubmitFilter = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();

      const { townCouncil, site, block, emergencyLight, startDate, endDate } =
        form.values;

      if (!townCouncil || !site || !block || !startDate || !endDate) {
        return;
      }

      if (endDate) {
        if (endDate < startDate) {
          setIsFiltered(false);
          return;
        }
      }

      dispatch(setFilter(form.values));

      onRefetchData(
        townCouncil ? townCouncil : null,
        site ? site : null,
        block ? block : null,
        emergencyLight ? emergencyLight : null,
        startDate ? moment.utc(startDate) : null,
        endDate ? moment.utc(endDate) : null
      );

      setDownloaDocumentPayload({
        townCouncil,
        site,
        block,
        emergencyLight,
        startDate,
        endDate,
      });

      setIsFiltered(true);
    }
  };

  const onClickDownload = (fileType: any) => {
    const form: any = formRef.current;

    if (form) {
      // form.handleSubmit();

      const { townCouncil, site, block, emergencyLight, startDate, endDate } =
        downloaDocumentPayload;

      if (!townCouncil || !site || !block || !startDate || !endDate) {
        return;
      }

      dispatch(
        downLoadTestReportsFile({
          fileType,
          tcCode: townCouncil ? townCouncil : null,
          siteCode: site ? site : null,
          blockCode: block ? block : null,
          emergencyLight: emergencyLight ? emergencyLight : null,
          startDate: startDate ? moment.utc(startDate) : null,
          endDate: endDate ? moment.utc(endDate) : null,
        })
      );
    }
  };

  const [formFilterInitialValues, setFormFilterInitialValues] = useState({
    townCouncil: "",
    site: "",
    block: "",
    emergencyLight: "",
    startDate: "",
    endDate: "",
  });

  const onReset = () => {
    const form: any = formRef.current;
    form.resetForm();

    setFilterUpdateCount(filterUpdateCount + 1);

    setSiteOptions([]);
    setBlockOptions([]);
    setIsFiltered(false);
    dispatch(setFilter({}));
    setFormFilterInitialValues({
      townCouncil: "",
      site: "",
      block: "",
      emergencyLight: "",
      startDate: "",
      endDate: "",
    });
  };

  const filterActionConfig: any = {
    label: "Search",
    event: onSubmitFilter,
    variant: "primary",
  };

  const reset: any = {
    label: "Reset",
    event: onReset,
    variant: "secondary",
  };

  const emptyConfig = {
    title: "No Test Found",
    description: "",
  };
  const currentDateTime = moment().format("YYYY-MM-DD");
  const yesterdayDateTime = moment().subtract(1, "day").format("YYYY-MM-DD");
  const date13MonthsAgo = moment().subtract(13, "months").format("YYYY-MM-DD");

  const filterFormConfig: FilterFormConfig = {
    controls: [
      {
        controlGroup: [
          {
            name: "townCouncil",
            placeholder: "Select Town Council *",
            type: "select",
            options: tcOptions,
            onChange: onChangeTC,
            required: true,
            isLoading: isLoadingTownCouncils,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "site",
            placeholder: "Select Site *",
            type: "select",
            options: siteOptions,
            onChange: onChangeSite,
            required: true,
            isLoading: isLocationSitesByTCCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "block",
            placeholder: "Select Block *",
            type: "select",
            options: blockOptions,
            onChange: onChangeBlock,
            required: true,
            isLoading: isLoadingBlocksBySiteCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "emergencyLight",
            placeholder: "Select Emergency Light",
            type: "select",
            options: emergencyLightOptions,
            required: true,
            isLoading: isLoadingEmergencyLightByBlockCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "startDate",
            placeholder: "Select Start Date *",
            type: "date",
            required: true,
            isLoading: false,
            onChange: onChangeStartDate,

            minDate: date13MonthsAgo,
            maxDate: yesterdayDateTime,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "endDate",
            placeholder: "Select End Date *",
            type: "date",
            required: true,
            isLoading: false,
            onChange: onChangeEndDate,
            minDate: date13MonthsAgo,
            maxDate: yesterdayDateTime,
          },
        ],
      },
    ],
  };

  const navigate = useNavigate();

  const redirectToConfiguration = () => {
    navigate("/admin/configuration/reportconfiguration");
  };

  const validationSchema = yup.object().shape({
    townCouncil: yup.string().required("Town Council is required"),
    site: yup.string().required("Site is required"),
    block: yup.string().required("Block is required"),
    // startDate: yup.date().required("Start Date is required"),
    // endDate: yup.date().required("End Date is required"),
    startDate: yup.date().required("Start Date is required"),
    endDate: yup
      .date()
      .min(yup.ref("startDate"), "End Date must be after Start Date")
      .required("End Date is required"),
  });

  return (
    <div>
      {/*       <div className="flex justify-end table-generic-action mb-2">
        <B2Button
          className="icon-text-btn"
          variant="primary"
          onClick={redirectToConfiguration}
        >
          Automatic Report Scheduler
          <FontAwesomeIcon icon={faCalendar} className="fs-7 ms-2" />
        </B2Button>
      </div> */}

      <div>
        <FilterFormComponent
          key={formFilterInitialValues?.townCouncil}
          formRef={formRef}
          initialValues={formFilterInitialValues}
          formConfig={filterFormConfig}
          actionConfig={filterActionConfig}
          resetButton={reset}
          validation={validationSchema}
          filterUpdateCount={filterUpdateCount}
        />
      </div>
      <div>
        {isFiltered && (
          <div className="my-3">
            <TableComponent
              tableHeaders={columns}
              emptyConfig={emptyConfig}
              data={data}
              totalNumberOfRecords={totalNumberOfRecords}
              pageSize={pageSize}
              current={current}
              onChangePage={onChangePage}
              onChangeSort={onChangeSort}
              isLoadingData={isLoading}
            />

            <div className="export py-4 d-flex align-items-center">
              <HasPermission
                moduleName="Test Report"
                actionName="Download-Test Report"
                isRoute={false}
              >
                <div className="d-flex gap-2">
                  <B2Button
                    variant="light"
                    onClick={() => onClickDownload("downloadPdf")}
                  >
                    <img className="fs-7 ms-2" src={pdfIcon} alt="PDF Icon" />{" "}
                    Export as PDF
                  </B2Button>

                  <B2Button
                    variant="light"
                    onClick={() => onClickDownload("downloadExcel")}
                  >
                    <img
                      className="fs-7 me-2"
                      src={spreadsheetIcon}
                      alt="Spreadsheet Icon"
                    />{" "}
                    Export as Excel
                  </B2Button>
                </div>

                {isLoadingTestReportsDownload ? (
                  <Spinner
                    className="ms-2"
                    style={{ color: "#1976D2" }}
                    animation="border"
                    role="status"
                    size="sm"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                ) : null}
              </HasPermission>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestReportTableComponent;
